import type { GsapTween } from "./index.types";
import { THREETween } from "./THREETween";
import gsap from "gsap";

type AnyTween = GsapTween | THREETween;

type TimelineChild = {
  tween: AnyTween;
  start: number;
};

/**
 * Lightweight timeline that orchestrates Tween children using GSAP's
 * own ticker (via `gsap.delayedCall`) instead of a custom RAF loop.
 *
 * Design mirrors Context.ts — delegates play/pause/reset/etc. to each
 * child — but adds position-based scheduling so children can start at
 * different times within the sequence.
 */

export class Timeline {
  public id: string;

  // tweens
  private children = new Map<string, TimelineChild>();
  private labels = new Map<string, number>();
  private pending: gsap.core.Tween[] = [];

  private repeatCount = 0;
  private repeatDelaySeconds = 0;
  private iteration = 0;
  private _playStart = 0;

  private callbacks: {
    onStart?: () => void;
    onUpdate?: () => void;
    onComplete?: () => void;
  } = {};

  private resolves: Array<() => void> = [];

  constructor(tweens: AnyTween[] = [], id?: string) {
    this.id = id ?? crypto.randomUUID();
    tweens.forEach((t) => this.add(t));
  }

  // ── children ────────────────────────────────────────────────

  add(tween: AnyTween, position?: number | string): this {
    const start = this.resolvePosition(position);
    this.children.set(tween.id!, { tween, start });
    return this;
  }

  addLabel(label: string, position?: number | string): this {
    this.labels.set(label, this.resolvePosition(position));
    return this;
  }

  getLabelTime(label: string): number | undefined {
    return this.labels.get(label);
  }

  time(): number {
    if (!this._playStart) return 0;
    return (performance.now() - this._playStart) / 1000;
  }

  removeLabel(label: string): number | null {
    const time = this.labels.get(label);
    if (time === undefined) return null;
    this.labels.delete(label);
    return time;
  }

  clear(): this {
    this.cancelPending();
    this.children.clear();
    this.labels.clear();
    this.iteration = 0;
    return this;
  }

  // ── config ──────────────────────────────────────────────────

  repeat(value?: number): number | void {
    if (value === undefined) return this.repeatCount;
    this.repeatCount = Math.floor(value);
  }

  repeatDelay(value?: number): number | void {
    if (value === undefined) return this.repeatDelaySeconds;
    this.repeatDelaySeconds = Math.max(0, value);
  }

  // ── playback ────────────────────────────────────────────────

  play(): this {
    this.cancelPending();
    this.iteration = 0;
    this._playStart = performance.now();
    this.scheduleChildren();
    return this;
  }

  pause(): this {
    this.pending.forEach((dc) => dc.pause());
    this.children.forEach((c) => c.tween.pause?.());
    return this;
  }

  resume(): this {
    this.pending.forEach((dc) => dc.resume());
    this.children.forEach((c) => c.tween.resume?.());
    return this;
  }

  invalidate(): this {
    this.children.forEach((c) => c.tween.invalidate?.());
    return this;
  }

  reset(): this {
    this.cancelPending();
    this.iteration = 0;
    this.children.forEach((c) => {
      c.tween.reset();
    });
    return this;
  }

  playPause(time?: number): this {
    this.play();
    if (time === undefined) {
      this.pause();
    } else {
      this.pending.push(
        gsap.delayedCall(Math.max(0, time), () => this.pause())
      );
    }
    return this;
  }

  // ── progress / duration ─────────────────────────────────────

  duration(): number {
    let max = 0;
    this.children.forEach((c) => {
      const delay = this.getDelay(c.tween);
      const end = c.start + delay + (c.tween.duration?.() ?? 0);
      if (end > max) max = end;
    });
    return max;
  }

  progress(value?: number): number {
    const d = this.duration();
    if (!d) return 0;

    if (value !== undefined) {
      this.seek(Math.min(Math.max(value, 0), 1) * d);
      return Math.min(Math.max(value, 0), 1);
    }

    // Derive progress from child states
    let furthest = 0;
    this.children.forEach((c) => {
      const p = c.tween.progress?.() ?? 0;
      const dur = c.tween.duration?.() ?? 0;
      furthest = Math.max(furthest, c.start + p * dur);
    });
    return Math.min(furthest / d, 1);
  }

  seek(time: number): this {
    this.cancelPending();
    const d = this.duration();
    const t = Math.min(Math.max(time, 0), d);

    this.children.forEach((c) => {
      const delay = this.getDelay(c.tween);
      const local = t - c.start - delay;
      const dur = c.tween.duration?.() ?? 0;

      if (local <= 0) {
        c.tween.progress?.(0);
      } else if (local >= dur) {
        c.tween.progress?.(1);
      } else {
        c.tween.progress?.(dur ? local / dur : 0);
      }
      c.tween.pause?.();
    });
    return this;
  }

  // ── cleanup ─────────────────────────────────────────────────

  kill(): this {
    this.cancelPending();
    this.children.forEach((c) => c.tween.kill?.());
    return this;
  }

  revert(): this {
    this.cancelPending();
    this.children.forEach((c) => c.tween.revert?.());
    return this;
  }

  // ── callbacks / promises ────────────────────────────────────

  eventCallback(
    type: "onStart" | "onUpdate" | "onComplete",
    cb: () => void
  ): this {
    this.callbacks[type] = cb;
    return this;
  }

  then(
    onFulfilled?: ((value: Omit<this, "then">) => any) | null,
    _onRejected?: ((reason: any) => any) | null
  ): Promise<this> {
    // Already done
    if (this.pending.length === 0 && this.children.size > 0) {
      let allDone = true;
      this.children.forEach((c) => {
        if ((c.tween.progress?.() ?? 0) < 1) allDone = false;
      });
      if (allDone) {
        onFulfilled?.(this);
        return Promise.resolve(this);
      }
    }

    return new Promise<this>((resolve) => {
      this.resolves.push(() => {
        onFulfilled?.(this);
        resolve(this);
      });
    });
  }

  // ── private ─────────────────────────────────────────────────

  private scheduleChildren() {
    const d = this.duration();
    this.callbacks.onStart?.();

    this.children.forEach((c) => {
      c.tween.reset?.();
      if (c.start <= 0) {
        c.tween.play?.();
      } else {
        this.pending.push(
          gsap.delayedCall(c.start, () => c.tween.play?.())
        );
      }
    });

    // Schedule completion
    if (d > 0) {
      this.pending.push(
        gsap.delayedCall(d, () => this.onIterationComplete())
      );
    }
  }

  private onIterationComplete() {
    if (this.repeatCount === -1 || this.iteration < this.repeatCount) {
      this.iteration += 1;
      this.cancelPending();
      this.children.forEach((c) => {
        c.tween.reset?.();
        c.tween.pause?.();
      });

      if (this.repeatDelaySeconds > 0) {
        this.pending.push(
          gsap.delayedCall(this.repeatDelaySeconds, () =>
            this.scheduleChildren()
          )
        );
      } else {
        this.scheduleChildren();
      }
      return;
    }

    this.callbacks.onComplete?.();
    this.resolveAll();
  }

  private resolveAll() {
    const items = this.resolves.splice(0, this.resolves.length);
    items.forEach((r) => r());
  }

  private cancelPending() {
    this.pending.forEach((dc) => dc.kill());
    this.pending = [];
  }

  private resolvePosition(position?: number | string): number {
    if (position === undefined || position === null) return this.duration();
    if (typeof position === "number") return Math.max(0, position);

    const label = this.labels.get(position);
    if (label !== undefined) return label;

    const entries = Array.from(this.children.values());
    const last = entries[entries.length - 1];
    const lastStart = last ? last.start : 0;
    const lastEnd = last ? last.start + this.getDelay(last.tween) + (last.tween.duration?.() ?? 0) : 0;

    if (position === "<") return lastStart;
    if (position === ">") return lastEnd;

    const relFromLast = position.startsWith("<") ? lastStart : position.startsWith(">") ? lastEnd : null;

    if (relFromLast !== null) {
      const offset = this.parseRelative(position.slice(1));
      return Math.max(0, relFromLast + offset);
    }

    const rel = this.parseRelative(position);
    if (!Number.isNaN(rel)) return Math.max(0, this.duration() + rel);

    const parsed = Number(position);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : this.duration();
  }

  private parseRelative(input: string): number {
    if (input.startsWith("+=")) return Number(input.slice(2));
    if (input.startsWith("-=")) return -Number(input.slice(2));
    return Number.NaN;
  }

  private getDelay(tween: AnyTween): number {
    const maybe = (tween as unknown as { delay?: (v?: number) => number | void | null }).delay;
    if (typeof maybe !== "function") return 0;
    const value = maybe.call(tween);
    return typeof value === "number" ? value : 0;
  }
}
